// src/pages/GroupsPage.tsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { groupsService } from "../services/groupsService";
import { usersService } from "../services/usersService";
import { expensesService } from "../services/expensesService";
import {
  HandlersGroupResponse,
  HandlersGroupCreate,
  HandlersAcquaintanceResponse,
  HandlersExpenseCreate,
  HandlersExpenseResponse,
  HandlersSplitTypeEnum,
} from "../generated";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  TextField,
  Typography,
  Chip,
  Avatar,
  Fab,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material";

// Currency options
const currencies = ["USD", "EUR", "GBP"];

// Format currency with symbol
const formatCurrency = (amount: number, currency: string) => {
  const currencySymbols: { [key: string]: string } = {
    USD: "$",
    EUR: "€",
    GBP: "£",
  };

  const symbol = currencySymbols[currency] || "";
  return `${symbol}${amount.toFixed(2)}`;
};

// Format date string
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "N/A";

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "Invalid Date";
  }
};

const GroupsPage: React.FC = () => {
  const { user } = useAuth();

  const [groups, setGroups] = useState<HandlersGroupResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [friends, setFriends] = useState<HandlersAcquaintanceResponse[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [groupExpenses, setGroupExpenses] = useState<HandlersExpenseResponse[]>(
    []
  );
  const [loadingExpenses, setLoadingExpenses] = useState(false);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteGroupDialog, setOpenDeleteGroupDialog] = useState(false);
  const [openDeleteExpenseDialog, setOpenDeleteExpenseDialog] = useState(false);
  const [openMembersDialog, setOpenMembersDialog] = useState(false);
  const [openAddExpenseDialog, setOpenAddExpenseDialog] = useState(false);
  const [openExpensesModal, setOpenExpensesModal] = useState(false);
  const [selectedGroup, setSelectedGroup] =
    useState<HandlersGroupResponse | null>(null);

  // Form states
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [selectedFriend, setSelectedFriend] =
    useState<HandlersAcquaintanceResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Expense form states
  const [expenseTitle, setExpenseTitle] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseCurrency, setExpenseCurrency] = useState("USD");
  const [expensePayer, setExpensePayer] = useState("");
  const [splitType, setSplitType] = useState<HandlersSplitTypeEnum>("equal");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSplitDetails] = useState<Record<string, number>>({});
  // Add these state variables to your component
  const [expenseToDelete, setExpenseToDelete] =
    useState<HandlersExpenseResponse | null>(null);
  const [deletingExpense, setDeletingExpense] = useState(false);

  const handleDeleteExpense = async () => {
    if (!expenseToDelete) return;

    try {
      setDeletingExpense(true);
      await expensesService.deleteExpense(expenseToDelete.public_id!);

      // Remove the expense from the local state
      const updatedExpenses = groupExpenses.filter(
        (expense) => expense.public_id !== expenseToDelete.public_id
      );
      setGroupExpenses(updatedExpenses);
      setSuccess("Expense deleted successfully");

      // Close the dialog
      handleCloseDeleteExpenseDialog();
    } catch (error) {
      console.error("Error deleting expense:", error);
      setError("Failed to delete expense. Please try again.");
    } finally {
      setDeletingExpense(false);
    }
  };

  // Pagination for expenses table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Load groups on component mount
  useEffect(() => {
    fetchGroups();
  }, []);

  // Fetch all groups
  const fetchGroups = async () => {
    setLoading(true);
    try {
      const data = await groupsService.getMyGroups();
      setGroups(data);
      setError("");
    } catch (err: any) {
      console.error("Failed to fetch groups:", err);
      setError("Failed to load groups. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch friends when needed
  const fetchFriends = async () => {
    if (!user?.public_id) return;

    setLoadingFriends(true);
    try {
      const data = await usersService.getUserFriends(user.public_id);
      setFriends(data);
    } catch (err: any) {
      console.error("Failed to fetch friends:", err);
      setError("Failed to load friends. Please try again later.");
    } finally {
      setLoadingFriends(false);
    }
  };

  // Fetch expenses for a group
  const fetchGroupExpenses = async (groupId: string) => {
    setLoadingExpenses(true);
    try {
      const data = await expensesService.getGroupExpenses(groupId);
      if (data) {
        setGroupExpenses(data);
      } else {
        setGroupExpenses([]);
      }
    } catch (err: any) {
      console.error("Failed to fetch group expenses:", err);
      setError("Failed to load expenses. Please try again later.");
    } finally {
      setLoadingExpenses(false);
    }
  };

  // Handle create group dialog
  const handleOpenCreateDialog = () => {
    setGroupName("");
    setGroupDescription("");
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  // Handle edit group dialog
  const handleOpenEditDialog = (group: HandlersGroupResponse) => {
    setSelectedGroup(group);
    setGroupName(group.name || "");
    setGroupDescription(group.description || "");
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedGroup(null);
  };

  // Handle delete group dialog
  const handleOpenDeleteGroupDialog = (group: HandlersGroupResponse) => {
    setSelectedGroup(group);
    setOpenDeleteGroupDialog(true);
  };

  const handleCloseDeleteGroupDialog = () => {
    setOpenDeleteGroupDialog(false);
    setSelectedGroup(null);
  };

  // Handle delete expense dialog
  const handleOpenDeleteExpenseDialog = (expense: HandlersExpenseResponse) => {
    setExpenseToDelete(expense);
    setOpenDeleteExpenseDialog(true);
  };

  const handleCloseDeleteExpenseDialog = () => {
    setOpenDeleteExpenseDialog(false);
    setExpenseToDelete(null);
  };

  // Handle members dialog
  const handleOpenMembersDialog = async (group: HandlersGroupResponse) => {
    setSelectedGroup(group);
    setOpenMembersDialog(true);
    setSelectedFriend(null);

    // Fetch friends list when opening the dialog
    await fetchFriends();
  };

  const handleCloseMembersDialog = () => {
    setSelectedGroup(null);
    setSelectedFriend(null);
    setOpenMembersDialog(false);
  };

  // Handle add expense dialog
  const handleOpenAddExpenseDialog = async (
    group: HandlersGroupResponse,
    event?: React.MouseEvent
  ) => {
    if (event) {
      event.stopPropagation(); // Prevent triggering the group click
    }

    setSelectedGroup(group);

    // Reset expense form fields
    resetExpenseForm();

    // Set current user as default payer
    if (user?.public_id) {
      setExpensePayer(user.public_id);
    }

    setOpenAddExpenseDialog(true);

    // Fetch expenses for the group
    await fetchGroupExpenses(group.public_id!);
  };

  const handleCloseAddExpenseDialog = () => {
    setOpenAddExpenseDialog(false);
    setSelectedGroup(null);
    resetExpenseForm();
  };

  // Handle expenses modal
  const handleOpenExpensesModal = async (group: HandlersGroupResponse) => {
    setSelectedGroup(group);
    setOpenExpensesModal(true);

    // Reset pagination
    setPage(0);

    // Fetch expenses for the group
    await fetchGroupExpenses(group.public_id!);
  };

  const handleCloseExpensesModal = () => {
    setOpenExpensesModal(false);
    setSelectedGroup(null);
    setGroupExpenses([]);
  };

  const resetExpenseForm = () => {
    setExpenseTitle("");
    setExpenseAmount("");
    setExpenseCurrency("GBP");
    setExpensePayer("");
    setSplitType("equal");
    setSplitDetails({});
  };

  // Create a new group
  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setError("Group name is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const newGroup: HandlersGroupCreate = {
        name: groupName.trim(),
        description: groupDescription.trim(),
        members: [],
      };

      await groupsService.createGroup(newGroup);
      await fetchGroups();
      setSuccess("Group created successfully");
      handleCloseCreateDialog();
    } catch (err: any) {
      console.error("Failed to create group:", err);
      setError(err.message || "Failed to create group");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update an existing group
  const handleUpdateGroup = async () => {
    if (!selectedGroup || !groupName.trim()) {
      setError("Group name is required");
      return;
    }

    setIsSubmitting(true);
    try {
      await groupsService.updateGroup(selectedGroup.public_id!, {
        name: groupName.trim(),
        description: groupDescription.trim(),
      });
      await fetchGroups();
      setSuccess("Group updated successfully");
      handleCloseEditDialog();
    } catch (err: any) {
      console.error("Failed to update group:", err);
      setError(err.message || "Failed to update group");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete a group
  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    setIsSubmitting(true);
    try {
      await groupsService.deleteGroup(selectedGroup.public_id!);
      setGroups(groups.filter((g) => g.public_id !== selectedGroup.public_id));
      setSuccess("Group deleted successfully");
      handleCloseDeleteGroupDialog();
    } catch (err: any) {
      console.error("Failed to delete group:", err);
      setError(err.message || "Failed to delete group");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add a member to group
  const handleAddMember = async () => {
    if (!selectedGroup || !selectedFriend) {
      setError("Please select a friend to add");
      return;
    }

    setIsSubmitting(true);
    try {
      await groupsService.addGroupMembers(selectedGroup.public_id!, [
        selectedFriend.user?.public_id!,
      ]);
      // Refresh the group to show updated members
      const updatedGroup = await groupsService.getGroup(
        selectedGroup.public_id!
      );
      setGroups(
        groups.map((g) =>
          g.public_id === selectedGroup.public_id ? updatedGroup : g
        )
      );
      setSelectedGroup(updatedGroup);
      setSelectedFriend(null);
      setSuccess("Member added successfully");
    } catch (err: any) {
      console.error("Failed to add member:", err);
      setError(err.message || "Failed to add member");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove a member from group
  const handleRemoveMember = async (userId: string) => {
    if (!selectedGroup) return;

    setIsSubmitting(true);
    try {
      await groupsService.removeGroupMember(selectedGroup.public_id!, userId);
      // Refresh the group to show updated members
      const updatedGroup = await groupsService.getGroup(
        selectedGroup.public_id!
      );
      setGroups(
        groups.map((g) =>
          g.public_id === selectedGroup.public_id ? updatedGroup : g
        )
      );
      setSelectedGroup(updatedGroup);
      setSuccess("Member removed successfully");
    } catch (err: any) {
      console.error("Failed to remove member:", err);
      setError(err.message || "Failed to remove member");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create a new expense
  const handleCreateExpense = async () => {
    if (
      !selectedGroup ||
      !expenseTitle.trim() ||
      !expenseAmount ||
      !expensePayer
    ) {
      setError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const amount = parseFloat(expenseAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error("Amount must be a positive number");
      }

      const expenseData: HandlersExpenseCreate = {
        description: expenseTitle.trim(),
        amount: amount,
        currency: expenseCurrency,
        paid_by: expensePayer,
        group_id: selectedGroup.public_id!,
        split_type: splitType,
        date_incurred: new Date().toISOString().split("T")[0], // Today's date in YYYY-MM-DD format
      };

      await expensesService.createExpense(expenseData);

      // Refresh the expenses for this group
      await fetchGroupExpenses(selectedGroup.public_id!);

      setSuccess("Expense created successfully");
      handleCloseAddExpenseDialog();
    } catch (err: any) {
      console.error("Failed to create expense:", err);
      setError(err.message || "Failed to create expense");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to get initials from name
  const getInitials = (name: string): string => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setError("");
    setSuccess("");
  };

  // Check if a user is already a member of the group
  const isAlreadyMember = (friendId: string | undefined): boolean => {
    if (!selectedGroup || !friendId) return false;
    return (
      selectedGroup.members?.some((member) => member.public_id === friendId) ||
      false
    );
  };

  // Filter friends to only show those who aren't already members of the selected group
  const availableFriends = friends.filter(
    (friend) => !isAlreadyMember(friend.user?.public_id)
  );

  return (
    <Container sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          color="primary"
          sx={{ fontWeight: "bold" }}
        >
          My Groups
        </Typography>
        <Tooltip title="Create New Group">
          <Fab
            color="primary"
            aria-label="add"
            onClick={handleOpenCreateDialog}
            sx={{ boxShadow: 2 }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : groups.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
          <GroupIcon
            sx={{ fontSize: 60, color: "primary.main", opacity: 0.6, mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            No Groups Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You haven't created or joined any expense sharing groups yet.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Create Your First Group
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {groups.map((group) => (
            <Box
              key={group.public_id}
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(33.333% - 16px)",
                },
                maxWidth: {
                  xs: "100%",
                  sm: "calc(50% - 12px)",
                  md: "calc(33.333% - 16px)",
                },
              }}
            >
              <Card
                onClick={() => handleOpenExpensesModal(group)}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: 4,
                    cursor: "pointer",
                  },
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "white",
                      color: "primary.main",
                      mr: 2,
                    }}
                  >
                    {getInitials(group.name || "")}
                  </Avatar>
                  <Typography variant="h6" component="h2" noWrap>
                    {group.name}
                  </Typography>
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {group.description || "No description provided"}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <PeopleIcon
                      fontSize="small"
                      color="primary"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="body2">
                      {group.members?.length || 0} Members
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 2 }}
                  >
                    {group.members?.slice(0, 3).map((member) => (
                      <Chip
                        key={member.public_id}
                        avatar={
                          <Avatar>{getInitials(member.name || "")}</Avatar>
                        }
                        label={member.name}
                        size="small"
                        sx={{ bgcolor: "rgba(230, 126, 34, 0.1)" }}
                      />
                    ))}
                    {(group.members?.length || 0) > 3 && (
                      <Chip
                        label={`+${(group.members?.length || 0) - 3} more`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </CardContent>

                <Divider />

                <CardActions
                  sx={{ justifyContent: "space-between", p: 1 }}
                  onClick={(e) => e.stopPropagation()} // Prevent card click when clicking actions
                >
                  <Box>
                    <Tooltip title="Edit Group">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(group);
                        }}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Group">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteGroupDialog(group);
                        }}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Add Expense">
                      <IconButton
                        onClick={(e) => handleOpenAddExpenseDialog(group, e)}
                        color="secondary"
                        size="small"
                      >
                        <ReceiptIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Tooltip title="Manage Members">
                    <Button
                      size="small"
                      startIcon={<PeopleIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenMembersDialog(group);
                      }}
                      color="secondary"
                      variant="outlined"
                    >
                      Members
                    </Button>
                  </Tooltip>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      {/* Create Group Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog}>
        <DialogTitle sx={{ color: "primary.main" }}>
          Create New Group
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a new expense sharing group to track expenses with friends,
            family, or colleagues.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Group Name"
            type="text"
            fullWidth
            variant="outlined"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            id="description"
            label="Description (Optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCreateDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleCreateGroup}
            variant="contained"
            color="primary"
            disabled={isSubmitting || !groupName.trim()}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Create Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <DialogTitle sx={{ color: "primary.main" }}>Edit Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="edit-name"
            label="Group Name"
            type="text"
            fullWidth
            variant="outlined"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            margin="dense"
            id="edit-description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            value={groupDescription}
            onChange={(e) => setGroupDescription(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEditDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateGroup}
            variant="contained"
            color="primary"
            disabled={isSubmitting || !groupName.trim()}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Group Dialog */}
      <Dialog
        open={openDeleteGroupDialog}
        onClose={handleCloseDeleteGroupDialog}
      >
        <DialogTitle sx={{ color: "error.main" }}>Delete Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the group "{selectedGroup?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteGroupDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteGroup}
            variant="contained"
            color="error"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Delete Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Members Dialog */}
      <Dialog
        open={openMembersDialog}
        onClose={handleCloseMembersDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: "primary.main" }}>
          Manage Members - {selectedGroup?.name}
        </DialogTitle>
        <DialogContent>
          {loadingFriends ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <CircularProgress size={30} color="primary" />
            </Box>
          ) : (
            <Autocomplete
              value={selectedFriend}
              onChange={(event, newValue) => {
                setSelectedFriend(newValue);
              }}
              options={availableFriends}
              getOptionLabel={(option) => option.user?.name || ""}
              isOptionEqualToValue={(option, value) =>
                option.user?.public_id === value.user?.public_id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Friend"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon color="primary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar sx={{ mr: 2, bgcolor: "secondary.main" }}>
                      {getInitials(option.user?.name || "")}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1">
                        {option.user?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.user?.email}
                      </Typography>
                    </Box>
                  </Box>
                </li>
              )}
              noOptionsText={
                <Typography color="text.secondary">
                  {friends.length === 0
                    ? "You don't have any friends yet"
                    : "All your friends are already members"}
                </Typography>
              }
            />
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAddMember}
              disabled={isSubmitting || !selectedFriend}
              startIcon={<PersonAddIcon />}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Add to Group"
              )}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" gutterBottom>
            Current Members
          </Typography>

          {selectedGroup?.members && selectedGroup.members.length > 0 ? (
            <List>
              {selectedGroup.members.map((member) => (
                <ListItem
                  key={member.public_id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="remove"
                      onClick={() => handleRemoveMember(member.public_id!)}
                      disabled={
                        isSubmitting || member.public_id === user?.public_id
                      }
                      color="error"
                    >
                      <PersonRemoveIcon />
                    </IconButton>
                  }
                  divider
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor:
                          member.public_id === user?.public_id
                            ? "primary.main"
                            : "secondary.main",
                      }}
                    >
                      {getInitials(member.name || "")}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <>
                        {member.name}
                        {member.public_id === user?.public_id && (
                          <Chip
                            label="You"
                            size="small"
                            color="primary"
                            sx={{ ml: 1, height: 20 }}
                          />
                        )}
                      </>
                    }
                    secondary={member.email}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 2 }}
            >
              No members found
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseMembersDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog
        open={openAddExpenseDialog}
        onClose={handleCloseAddExpenseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{ color: "primary.main", display: "flex", alignItems: "center" }}
        >
          <ReceiptIcon sx={{ mr: 1 }} />
          Add Expense to {selectedGroup?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3 }}>
              <TextField
                autoFocus
                margin="normal"
                id="expense-title"
                label="Expense Title"
                type="text"
                fullWidth
                variant="outlined"
                value={expenseTitle}
                onChange={(e) => setExpenseTitle(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ReceiptIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <TextField
                id="expense-amount"
                label="Amount"
                type="number"
                variant="outlined"
                fullWidth
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl variant="outlined" sx={{ minWidth: 120 }}>
                <InputLabel id="currency-label">Currency</InputLabel>
                <Select
                  labelId="currency-label"
                  id="currency"
                  value={expenseCurrency}
                  onChange={(e) => setExpenseCurrency(e.target.value)}
                  label="Currency"
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="payer-label">Paid By</InputLabel>
                <Select
                  labelId="payer-label"
                  id="payer"
                  value={expensePayer}
                  onChange={(e) => setExpensePayer(e.target.value)}
                  label="Paid By"
                >
                  {selectedGroup?.members?.map((member) => (
                    <MenuItem
                      key={member.public_id}
                      value={member.public_id || ""}
                    >
                      {member.name}{" "}
                      {member.public_id === user?.public_id ? "(You)" : ""}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Who paid for this expense?</FormHelperText>
              </FormControl>
            </Box>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                How should this expense be split?
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={splitType}
                  onChange={(e) =>
                    setSplitType(e.target.value as HandlersSplitTypeEnum)
                  }
                >
                  <FormControlLabel
                    value="equal"
                    control={<Radio color="primary" />}
                    label="Split equally among all members"
                  />
                  <FormControlLabel
                    value="percentage"
                    control={<Radio color="primary" disabled />}
                    label="Split by percentage (Coming soon)"
                  />
                  <FormControlLabel
                    value="amount"
                    control={<Radio color="primary" disabled />}
                    label="Split by exact amounts (Coming soon)"
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* Future: Add custom split UI based on splitType */}
            {splitType !== "equal" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Custom split options will be available in a future update. For
                now, all expenses are split equally among group members.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAddExpenseDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleCreateExpense}
            variant="contained"
            color="primary"
            disabled={
              isSubmitting ||
              !expenseTitle.trim() ||
              !expenseAmount ||
              !expensePayer
            }
            startIcon={<ReceiptIcon />}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "Add Expense"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Expenses Modal */}
      <Dialog
        open={openExpensesModal}
        onClose={handleCloseExpensesModal}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            bgcolor: "primary.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ReceiptIcon sx={{ mr: 1 }} />
            Expenses for {selectedGroup?.name}
          </Box>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => {
              handleCloseExpensesModal();
              if (selectedGroup) {
                handleOpenAddExpenseDialog(selectedGroup);
              }
            }}
            sx={{
              borderColor: "white",
              "&:hover": {
                borderColor: "white",
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Add Expense
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loadingExpenses ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : groupExpenses.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <ReceiptIcon
                sx={{
                  fontSize: 60,
                  color: "primary.main",
                  opacity: 0.6,
                  mb: 2,
                }}
              />
              <Typography variant="h6" gutterBottom>
                No Expenses Found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                This group doesn't have any expenses yet.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  handleCloseExpensesModal();
                  if (selectedGroup) {
                    handleOpenAddExpenseDialog(selectedGroup);
                  }
                }}
              >
                Add First Expense
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table aria-label="expenses table">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "rgba(0, 0, 0, 0.04)" }}>
                      <TableCell>Description</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Paid By</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Split</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupExpenses
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((expense) => (
                        <TableRow
                          key={expense.public_id}
                          sx={{ "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" } }}
                        >
                          <TableCell>{expense.description}</TableCell>
                          <TableCell>
                            <Typography
                              fontWeight="medium"
                              color={
                                (expense.amount ?? 0) > 50
                                  ? "error.main"
                                  : "text.primary"
                              }
                            >
                              {formatCurrency(
                                expense.amount ?? 0,
                                expense.currency || "USD"
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Avatar
                                sx={{
                                  width: 24,
                                  height: 24,
                                  mr: 1,
                                  fontSize: "0.75rem",
                                  bgcolor:
                                    expense.paid_by === user?.public_id
                                      ? "primary.main"
                                      : "secondary.main",
                                }}
                              >
                                {getInitials(expense.paid_by?.name || "")}
                              </Avatar>
                              <Typography variant="body2">
                                {expense.paid_by?.name}
                                {expense.paid_by === user?.public_id &&
                                  " (You)"}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {formatDate(expense.date_incurred)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                expense.split_type ??
                                "".charAt(0).toUpperCase() +
                                  expense.split_type ??
                                "".slice(1)
                              }
                              size="small"
                              color={
                                expense.split_type === "equal"
                                  ? "primary"
                                  : "secondary"
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            {/* TODO view details to show drilldown of who owes what for the expense */}
                            {/* <Tooltip title="View Details">
                              <IconButton size="small" color="primary">
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip> */}
                            {
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleOpenDeleteExpenseDialog(expense)
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={groupExpenses.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Expense Confirmation Dialog */}
      <Dialog
        open={openDeleteExpenseDialog}
        onClose={handleCloseDeleteExpenseDialog}
        aria-labelledby="delete-expense-dialog-title"
      >
        <DialogTitle id="delete-expense-dialog-title" sx={{ pt: 3 }}>
          <Box display="flex" alignItems="center">
            <DeleteIcon color="error" sx={{ mr: 1 }} />
            Delete Expense
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this expense?
          </Typography>
          {expenseToDelete && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "rgba(0, 0, 0, 0.04)",
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {expenseToDelete.description}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCurrency(
                  expenseToDelete.amount ?? 0,
                  expenseToDelete.currency || "USD"
                )}{" "}
                • {formatDate(expenseToDelete.date_incurred)}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone. All expense shares and settlement
            information will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDeleteExpenseDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteExpense}
            disabled={deletingExpense}
          >
            {deletingExpense ? "Deleting..." : "Delete Expense"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars for feedback */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GroupsPage;
